import React, { useState } from "react";
import { CHART_1, CHART_2, CHART_3, INK, MUTED, GRID, PAPER } from "./palette";
import ModeWord from "./ModeWord";

// ---- simple_slopes: moderation's fitted lines at three values of the moderator, over the raw data ----
// Props from ChartData#simple_slopes: points [[x, y]] (every case), lines [{ level: low|mean|high, w,
// slope, p, from: [x, ŷ], to: [x, ŷ] }] (the model's prediction at the moderator's mean and one SD
// either side, across X's observed range), interaction (b3) and interactionP, focalName,
// moderatorName, outcomeName; xUnit/yUnit/wUnit (one unit of X, Y and the moderator, e.g. "PSS point")
// from settings.
// Tokens, axes and tooltips follow ConceptChart.jsx; the data are gray so the three lines carry the idea.
const W = 640, TOP = 16, RIGHT = 16;
const COLORS = { low: CHART_1, mean: CHART_2, high: CHART_3 };
const WORDS = { low: "Low", mean: "Average", high: "High" };
const SD_WORDS = { low: "one SD below the mean", mean: "the mean", high: "one SD above the mean" };

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
const fmtR = (x, places = 3) => fmt(x, places).replace(/^(−?)0\./, "$1.");
const fmtP = (p) => (p < 0.001 ? "p < .001" : `p = ${fmtR(p, 3)}`);
const scale = (ax, a, b) => (v) => a + ((v - ax.min) / (ax.max - ax.min)) * (b - a);
// Lowercase a column name for the middle of a sentence, but keep acronyms like GAD-7.
const lower = (s) => s.split(" ").map((w) => (/^[A-Z][a-z]+$/.test(w) ? w.toLowerCase() : w)).join(" ");
const units = (unit, v = 2) => (unit ? ` ${v === 1 ? unit : `${unit}s`}` : "");

// Nice bounds and ticks (as ConceptChart's axis()); a non-negative scale doesn't run below zero.
function axis(xs, pad = 0.05) {
	const lo = Math.min(...xs), hi = Math.max(...xs), span = hi - lo || 1;
	const rough = span / 6, mag = 10 ** Math.floor(Math.log10(rough));
	const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rough);
	const min = Math.max(lo >= 0 ? 0 : -Infinity, Math.floor((lo - span * pad) / step) * step), max = Math.ceil((hi + span * pad) / step) * step;
	const ticks = [];
	for (let t = min; t <= max + step / 2; t += step) ticks.push(+t.toFixed(10));
	return { min, max, ticks };
}

// Push end labels apart so none sits closer than `gap` to the one above it.
function spread(items, gap) {
	const sorted = [...items].sort((a, b) => a.y - b.y);
	for (let i = 1; i < sorted.length; i += 1) sorted[i].y = Math.max(sorted[i].y, sorted[i - 1].y + gap);
	return sorted;
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, 16), W - RIGHT - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${TOP})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

export default function SimpleSlopesChart({ points, lines, interaction, interactionP, focalName, moderatorName, outcomeName, xUnit, yUnit, wUnit, noun = "participant" }) {
	const [hover, setHover] = useState(null);
	const height = 340, box = { l: 44, r: W - 112, t: TOP + 18, b: height - 40 };
	const axX = axis(points.map((p) => p[0]), 0);
	const axY = axis([...points.map((p) => p[1]), ...lines.flatMap((l) => [l.from[1], l.to[1]])]);
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const focal = lower(focalName), moderator = lower(moderatorName), outcome = lower(outcomeName);
	// People with the same pair of scores share a dot, drawn darker the more of them there are.
	const stacked = Object.values(points.reduce((acc, [a, b]) => {
		const k = `${a},${b}`;
		acc[k] = acc[k] || { a, b, n: 0 };
		acc[k].n += 1;
		return acc;
	}, {}));
	const name = (l) => `${WORDS[l.level]} ${moderator} (${l.w.toFixed(2)})`;
	const ends = spread(lines.map((l) => ({ l, y: y(l.to[1]) })), 30);
	const per = `${yUnit ? units(yUnit, 2).trim() : outcome} per ${xUnit || `unit of ${focal}`}`;
	const lineTip = (l) => ({ cx: x(l.to[0]) - 180, level: l.level, lines: [`${WORDS[l.level]} ${moderator}: ${l.w.toFixed(2)}, ${SD_WORDS[l.level]}`,
		`Slope ${fmt(l.slope)} ${per}, ${fmtP(l.p)}`,
		`Predicted ${outcome}: ${fmt(l.from[1])} at ${focal} ${fmt(l.from[0], 0)}, ${fmt(l.to[1])} at ${fmt(l.to[0], 0)}`] });
	// Where the low and high lines meet: left of it their order flips.
	const lo = lines.find((l) => l.level === "low"), hi = lines.find((l) => l.level === "high");
	const meet = lo && hi && lo.slope !== hi.slope ? lo.from[0] + (hi.from[1] - lo.from[1]) / (lo.slope - hi.slope) : null;
	const meetsInside = meet !== null && meet > lo.from[0] && meet < lo.to[0];
	const below = meetsInside ? points.filter((p) => p[0] < meet).length : 0;
	const slopes = lines.map((l) => `${fmt(l.slope)} at ${moderator} ${l.w.toFixed(2)}`).join(", ");
	return (
		<figure>
			<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
				<span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: MUTED, opacity: 0.5 }} />One {noun}</span>
				{lines.map((l) => (
					<span key={l.level} className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded" style={{ background: COLORS[l.level] }} />{name(l)}</span>
				))}
			</div>
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Simple-slopes plot: ${points.length} ${noun}s' ${outcome} against their ${focal}, with the model's predicted ${outcome} at three levels of ${moderator}, drawn across the observed ${focal} scores, ${fmt(lo.from[0], 0)} to ${fmt(lo.to[0], 0)}. The slopes are ${slopes}: the lines fan out because the interaction coefficient is ${fmt(interaction)}.`}>
				{axY.ticks.map((t) => (
					<g key={`y${t}`}>
						<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{t}</text>
					</g>
				))}
				{axX.ticks.map((t) => <text key={`x${t}`} x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{t}</text>)}
				<text x={box.r} y={box.b + 32} fontSize="11" fill={MUTED} textAnchor="end">{focalName}</text>
				<text x={box.l} y={box.t - 8} fontSize="11" fill={MUTED}>{outcomeName}</text>
				{stacked.map(({ a, b, n }) => (
					<circle key={`${a},${b}`} cx={x(a)} cy={y(b)} r="3.5" fill={MUTED} stroke={PAPER} strokeWidth="1"
						opacity={(hover?.level ? 0.15 : 1) * Math.min(0.85, 0.3 + 0.15 * (n - 1))}
						onMouseEnter={() => setHover({ cx: x(a), lines: [n === 1 ? `One ${noun}` : `${n} ${noun}s with these scores`, `${focalName}: ${a}`, `${outcomeName}: ${b}`] })} onMouseLeave={() => setHover(null)} />
				))}
				{lines.map((l) => (
					<g key={l.level} onMouseEnter={() => setHover(lineTip(l))} onMouseLeave={() => setHover(null)}>
						<line x1={x(l.from[0])} x2={x(l.to[0])} y1={y(l.from[1])} y2={y(l.to[1])} stroke="transparent" strokeWidth="12" />
						<line x1={x(l.from[0])} x2={x(l.to[0])} y1={y(l.from[1])} y2={y(l.to[1])} stroke={COLORS[l.level]}
							strokeWidth={hover?.level === l.level ? 3.5 : 2.5} strokeLinecap="round" opacity={hover?.level && hover.level !== l.level ? 0.4 : 1} />
					</g>
				))}
				{ends.map(({ l, y: ly }) => (
					<g key={l.level}>
						<text x={box.r + 10} y={ly} fontSize="12" fill={INK} fontWeight="600" dominantBaseline="middle">{WORDS[l.level]} ({l.w.toFixed(2)})</text>
						<text x={box.r + 10} y={ly + 14} fontSize="11" fill={MUTED} dominantBaseline="middle">Slope {fmt(l.slope)}</text>
					</g>
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each dot is one {noun} (<ModeWord light="darker" dark="brighter" /> dots are several {noun}s with the same scores). Each line is the model's predicted {outcome} for {noun}s
				with {moderator} at {lines.map((l) => `${l.w.toFixed(2)} (${SD_WORDS[l.level]})`).join(", ").replace(/, ([^,]*)$/, " or $1")}, drawn only across the {focal} scores
				people reported. The lines' slopes are the simple slopes, {lines.map((l) => fmt(l.slope)).join(", ").replace(/, ([^,]*)$/, " and $1")}{yUnit ? ` ${units(yUnit).trim()}` : ""} per {xUnit || `unit of ${focal}`}.
				They fan out because the interaction coefficient is {fmt(interaction)} ({fmtP(interactionP)}): {focal}'s slope is {fmt(Math.abs(interaction))} {interaction < 0 ? "shallower" : "steeper"} among {noun}s one {wUnit || `unit of ${moderator}`} higher.
				{meetsInside ? ` The lines meet at a ${focal} score of about ${fmt(meet, 1)}; to the left of it, where ${below} of the ${points.length} ${noun}s score, their order flips.` : ""}
			</figcaption>
		</figure>
	);
}
